import type {
  CreateAccountInput,
  LoginResponse,
  RecaptchaInput,
} from "~/types/auth";

export type VideoCallProvider = string;
export type VideoCallMode = string;
export type VideoCallStatus = "active" | "ended" | "expired";
export type VideoCallRole = "host" | "guest";
export type VideoCallParticipantState =
  | "admitted"
  | "connected"
  | "disconnected"
  | "left"
  | "removed";
export type VideoCallJoinRequestStatus =
  | "pending"
  | "approved"
  | "canceled"
  | "cancelled"
  | "denied"
  | "expired";
export type VideoCallConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "failed";

export type VideoCallTeacherLinkStatus =
  | "waiting_for_admission"
  | "linked"
  | "capacity_reached"
  | "not_applicable";

/**
 * Identifies one negotiated media slot. The mesh creates exactly these
 * transceivers, in this order, so both peers correlate incoming tracks by
 * transceiver identity instead of by track kind.
 */
export type VideoCallMediaSlot = "microphone" | "camera" | "screen";

export const VIDEO_CALL_MEDIA_SLOT_ORDER: readonly VideoCallMediaSlot[] = [
  "microphone",
  "camera",
  "screen",
] as const;

/** Slots that carry a remote video track a tile can render. */
export type VideoCallVideoSlot = Exclude<VideoCallMediaSlot, "microphone">;

/**
 * Size of the floating call panel. All three render video: `small` replaced the
 * old status-only minimized bar. Only `large` stays anchored — the smaller two
 * can be dragged around the viewport.
 */
export type VideoCallPanelSize = "small" | "medium" | "large";

export const VIDEO_CALL_PANEL_SIZE_ORDER: readonly VideoCallPanelSize[] = [
  "small",
  "medium",
  "large",
] as const;

export const DEFAULT_VIDEO_CALL_PANEL_SIZE: VideoCallPanelSize = "medium";

export type VideoCallScreenShareDeniedReason =
  | "presenter_busy"
  | "not_allowed"
  | "unsupported"
  | "dismissed"
  | "failed";

export type VideoCallPeerRoute = "direct" | "relay" | "unknown";
export type VideoCallPeerProtocol = "udp" | "tcp" | "tls" | "mixed" | null;
export type VideoCallQualityProfile =
  | "focus"
  | "standard"
  | "low"
  | "survival"
  | "paused";
export type VideoCallQualityReason =
  | "default"
  | "receiver"
  | "visibility"
  | "audio"
  | "bandwidth"
  | "cpu"
  | "recovery";
export type VideoCallAudioHealth =
  | "healthy"
  | "warning"
  | "critical"
  | "unknown";
export type VideoCallQualityLimitationReason =
  | "none"
  | "cpu"
  | "bandwidth"
  | "other";

export interface VideoCallAdaptiveMetrics {
  audioPacketLossPercent: number | null;
  audioConcealmentPercent: number | null;
  /** Raw receiver-side audio loss used to request less remote video. */
  inboundAudioPacketLossPercent?: number | null;
  /** Raw remote receiver report used to protect this sender's audio. */
  outboundAudioPacketLossPercent?: number | null;
  /** Raw local concealment used to protect the received audio direction. */
  inboundAudioConcealmentPercent?: number | null;
  /** False is a repeated browser report; undefined preserves legacy callers. */
  inboundAudioSampleFresh?: boolean;
  /** False is a repeated RTCP report; undefined preserves legacy callers. */
  outboundAudioSampleFresh?: boolean;
  /** Fresh outbound video evidence, independent of slower audio RTCP reports. */
  videoSampleFresh?: boolean;
  jitterMs: number | null;
  outboundBitrateKbps: number | null;
  inboundBitrateKbps: number | null;
  availableOutgoingBitrateKbps: number | null;
  videoFramesPerSecond: number | null;
  videoFramesDroppedPerSecond: number | null;
  videoEncodeTimeMsPerFrame: number | null;
  videoTargetBitrateKbps: number | null;
  audioLevel: number | null;
  qualityLimitationReason: VideoCallQualityLimitationReason | null;
}

export interface VideoCallPeerDiagnostics {
  route: VideoCallPeerRoute;
  protocol: VideoCallPeerProtocol;
  roundTripTimeMs: number | null;
  packetLossPercent: number | null;
  degraded: boolean;
  collectedAt: string;
  adaptive?: VideoCallAdaptiveMetrics;
  qualityProfile?: VideoCallQualityProfile;
  qualityReason?: VideoCallQualityReason;
  audioHealth?: VideoCallAudioHealth;
}

export interface VideoCallAdaptiveMediaConfiguration {
  version: 1;
  enabled: boolean;
  quality_hints: boolean;
  qoe_reporting: boolean;
  report_interval_seconds: number;
}

export interface VideoCallScreenShareConfiguration {
  version: 1;
  enabled: boolean;
  /** When false only the host may claim the presenter slot. */
  allow_guests: boolean;
  /** Whether the presenter may publish tab/system audio alongside video. */
  audio: boolean;
}

export interface VideoCallProviderData extends Record<string, unknown> {
  adaptive_media?: VideoCallAdaptiveMediaConfiguration;
  screen_share?: VideoCallScreenShareConfiguration;
}

/** Single-presenter arbitration state owned by the backend. */
export interface VideoCallScreenShareState {
  participant_id: string | null;
  started_at?: string | null;
}

export type VideoCallInputDeviceKind = "audioinput" | "videoinput";
export type VideoCallMediaDevicesStatus =
  | "idle"
  | "loading"
  | "ready"
  | "unsupported"
  | "error";

export interface VideoCallInputDevice {
  deviceId: string;
  groupId: string;
  kind: VideoCallInputDeviceKind;
  label: string;
}

export interface VideoCallParticipant {
  id: string;
  name: string;
  role: VideoCallRole;
  state: VideoCallParticipantState;
  is_connected: boolean;
  /** Ephemeral media runtime identity; stable across signaling reconnects. */
  media_session_id?: string | null;
  room_id?: string;
  room_revision?: number;
  account_linked?: boolean;
  camera_enabled?: boolean;
  microphone_enabled?: boolean;
  /** Authoritative presenter flag. Never infer sharing from track presence. */
  screen_sharing?: boolean;
  joined_at?: string | null;
}

export interface VideoCallInvitationSummary {
  id: string;
  expires_at: string;
}

export interface VideoCallInvitation extends VideoCallInvitationSummary {
  token: string;
  share_url?: string;
  url?: string;
}

export interface VideoCall {
  id: string;
  status: VideoCallStatus;
  provider: VideoCallProvider;
  started_at: string;
  expires_at: string;
  ended_at?: string | null;
  invitation?: VideoCallInvitationSummary | null;
  participants?: VideoCallParticipant[];
  participant_count: number;
  organization_id?: number | null;
}

export interface VideoCallLimits {
  warning_participants: number;
  max_participants: number;
}

export interface VideoCallSignalingConfiguration {
  url: string;
  ticket: string;
  expires_at: string;
  subprotocols: string[];
  /** Backend-authorized grace for already connected media during a WS outage. */
  media_reconnect_grace_seconds?: number;
  heartbeat_interval_ms?: number;
  heartbeat_timeout_ms?: number;
}

export interface VideoCallRtcConfiguration {
  iceServers: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  credentials_expires_at?: string | null;
}

export interface VideoCallRoom {
  id: string;
  name: string;
  is_main: boolean;
  order: number;
}

export interface VideoCallRoomHelpRequest {
  participant_id: string;
  room_id: string;
  created_at: string;
}

export interface VideoCallRoomAnnouncement {
  id: string;
  body: string;
  server_sent_at: string;
}

/** Global roster and planned assignments are returned only to the host. */
export interface VideoCallRoomsSnapshot {
  version: 1;
  revision: number;
  state: "prepared" | "open" | "closing";
  main_room_id: string;
  current_room_id: string;
  membership_revision: number;
  rooms: VideoCallRoom[];
  roster?: VideoCallParticipant[];
  planned_assignments?: Record<string, string>;
  host_present: boolean;
  help_requests: VideoCallRoomHelpRequest[];
  announcement: VideoCallRoomAnnouncement | null;
  closing_deadline: string | null;
  server_time: string;
}

export interface VideoCallConnectionConfiguration {
  provider: VideoCallProvider;
  mode: VideoCallMode;
  participant: VideoCallParticipant;
  participants: VideoCallParticipant[];
  signaling: VideoCallSignalingConfiguration;
  rtc_configuration: VideoCallRtcConfiguration;
  limits: VideoCallLimits;
  provider_data?: VideoCallProviderData;
  rooms?: VideoCallRoomsSnapshot;
}

export interface VideoCallRoomsSaveInput {
  rooms: VideoCallRoom[];
  assignments: Record<string, string>;
}

/** A message is authorized for one entry into a room, not just its name. */
export interface VideoCallRoomScope {
  room_id?: string;
  source_room_revision?: number;
  target_room_revision?: number;
}

export interface VideoCallResponse {
  call: VideoCall;
  invitation?: VideoCallInvitation | null;
  connection?: VideoCallConnectionConfiguration;
}

export interface VideoCallCreateResponse {
  call: VideoCall;
  invitation: VideoCallInvitation;
}

export interface VideoCallCurrentResponse {
  call: VideoCall | null;
}

export interface VideoCallConnectionResponse {
  participant: VideoCallParticipant | null;
  connection?: VideoCallConnectionConfiguration;
}

export interface VideoCallInvitationResponse {
  invitation: VideoCallInvitation;
}

export interface VideoCallInvitationPreview {
  id?: string;
  call_id?: string;
  host_name: string;
  status: VideoCallStatus;
  expires_at: string;
  participant_count: number;
  limits: VideoCallLimits;
  organization_id?: number | null;
}

export interface VideoCallJoinRequest {
  id: string;
  call_id: string;
  name: string;
  status: VideoCallJoinRequestStatus;
  account_linked?: boolean;
  created_at: string;
  decided_at?: string | null;
  connection?: VideoCallConnectionConfiguration;
}

export interface VideoCallJoinRequestsResponse {
  join_requests: VideoCallJoinRequest[];
}

export interface VideoCallJoinResponse {
  join_request: VideoCallJoinRequest;
  guest_secret: string;
}

export interface VideoCallJoinRequestResponse {
  join_request: VideoCallJoinRequest;
  call?: VideoCall;
  participant?: VideoCallParticipant | null;
  connection?: VideoCallConnectionConfiguration;
  self_participant_id?: string;
  teacher_link_status?: VideoCallTeacherLinkStatus;
}

export interface VideoCallStudentSignupEligibility {
  eligible: boolean;
  teacher_link_status?: VideoCallTeacherLinkStatus;
}

export type VideoCallStudentPasswordSignupInput = CreateAccountInput;

export interface VideoCallStudentGoogleSignupInput {
  credential: string;
  recaptcha: RecaptchaInput;
}

export interface VideoCallStudentSignupResponse
  extends LoginResponse, VideoCallJoinRequestResponse {
  account_created: boolean;
  teacher_link_status: VideoCallTeacherLinkStatus;
}

export interface VideoCallChatParticipant {
  id: string;
  name: string;
  role: VideoCallRole;
}

export interface VideoCallChatMessage extends VideoCallRoomScope {
  sequence: number;
  message_id: string;
  participant: VideoCallChatParticipant;
  body: string;
  server_sent_at: string;
}

export interface VideoCallChatCapability {
  enabled: boolean;
  retention: "active_call";
  max_body_length: number;
  messages: VideoCallChatMessage[];
}

export interface VideoCallRoomsResponse {
  rooms: VideoCallRoomsSnapshot;
  participant: VideoCallParticipant;
  participants: VideoCallParticipant[];
  screen_share?: VideoCallScreenShareState;
  chat?: VideoCallChatCapability;
}

export interface VideoCallChatClientMessageEvent extends VideoCallRoomScope {
  type: "chat.message";
  message_id: string;
  body: string;
  client_sent_at?: string;
}

export interface VideoCallChatMessageEvent extends VideoCallChatMessage {
  type: "chat.message";
}

export interface VideoCallChatMessageAckEvent extends VideoCallRoomScope {
  type: "chat.message.ack";
  message: VideoCallChatMessage;
}

export interface VideoCallChatMessageRejectedEvent extends VideoCallRoomScope {
  type: "chat.message.rejected";
  message_id: string;
  code?: string;
  detail?: string;
  retry_after_ms?: number;
}

export type VideoCallChatDeliveryStatus = "pending" | "sent" | "failed";

export interface VideoCallChatDisplayMessage {
  sequence: number | null;
  message_id: string;
  participant: VideoCallChatParticipant;
  body: string;
  client_sent_at?: string;
  server_sent_at?: string;
  status: VideoCallChatDeliveryStatus;
  error?: string;
}

export interface VideoCallServerReadyEvent {
  type: "session.ready";
  call: VideoCall;
  participant: VideoCallParticipant;
  self_participant_id: string;
  participants: VideoCallParticipant[];
  join_requests?: VideoCallJoinRequest[];
  limits?: VideoCallLimits;
  adaptive_media?: VideoCallAdaptiveMediaConfiguration;
  screen_share?: VideoCallScreenShareState;
  chat?: VideoCallChatCapability;
  rooms?: VideoCallRoomsSnapshot;
}

export interface VideoCallSignalEvent extends VideoCallRoomScope {
  type: "offer" | "answer";
  target_participant_id?: string;
  source_participant_id?: string;
  source_media_session_id?: string;
  sdp: RTCSessionDescriptionInit;
}

export interface VideoCallIceCandidateEvent extends VideoCallRoomScope {
  type: "ice_candidate";
  target_participant_id?: string;
  source_participant_id?: string;
  source_media_session_id?: string;
  candidate: RTCIceCandidateInit | null;
}

export interface VideoCallQualityRequestEvent extends VideoCallRoomScope {
  type: "video.quality.request";
  target_participant_id?: string;
  source_participant_id?: string;
  source_media_session_id?: string;
  profile: VideoCallQualityProfile;
}

export interface VideoCallQualityRetryEvent extends VideoCallRoomScope {
  type: "video.quality.retry";
  target_participant_id: string;
  profile: VideoCallQualityProfile;
  retry_after_ms: number;
}

/** Broadcast whenever the arbitrated presenter slot changes owner. */
export interface VideoCallScreenShareStateEvent
  extends VideoCallScreenShareState, VideoCallRoomScope {
  type: "screen_share.state";
}

/** Sent only to the claimant when the presenter slot could not be taken. */
export interface VideoCallScreenShareRejectedEvent extends VideoCallRoomScope {
  type: "screen_share.rejected";
  reason: Extract<
    VideoCallScreenShareDeniedReason,
    "presenter_busy" | "not_allowed"
  >;
  /** Present when the slot is held by someone else. */
  participant_id?: string | null;
}

export type VideoCallServerEvent =
  | VideoCallServerReadyEvent
  | (VideoCallRoomsResponse & { type: "rooms.snapshot" })
  | VideoCallChatMessageEvent
  | VideoCallChatMessageAckEvent
  | VideoCallChatMessageRejectedEvent
  | VideoCallSignalEvent
  | VideoCallIceCandidateEvent
  | VideoCallQualityRequestEvent
  | VideoCallQualityRetryEvent
  | VideoCallScreenShareStateEvent
  | VideoCallScreenShareRejectedEvent
  | {
      type:
        | "participant.connected"
        | "participant.disconnected"
        | "participant.left"
        | "participant.removed"
        | "participant.updated";
      participant?: VideoCallParticipant;
      participant_id?: string;
    }
  | {
      type: "join_request.created" | "join_request.updated";
      join_request: VideoCallJoinRequest;
    }
  | { type: "call.ended"; call_id?: string }
  | {
      type: "pong";
      adaptive_media?: VideoCallAdaptiveMediaConfiguration;
      rooms?: VideoCallRoomsSnapshot;
    }
  | { type: "error"; code?: string; detail?: string };
